import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { KeyRound, Check } from "lucide-react";
import api from "../lib/api";
import { useI18n } from "../context/I18nContext";

export default function ResetPin() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setError("");
    if (![4, 6].includes(pin.length)) { setError(t("reset.invalid_pin")); return; }
    if (pin !== confirm) { setError(t("reset.mismatch")); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-pin", { token, new_pin: pin });
      setDone(true);
    } catch (e) {
      setError(e.response?.data?.detail || t("reset.bad_link"));
    } finally { setLoading(false); }
  };

  const field = (label, value, set) => (
    <div>
      <label className="block text-xs text-zinc-400 uppercase tracking-wider mb-1">{label}</label>
      <input type="password" inputMode="numeric" value={value}
        onChange={(e) => set(e.target.value.replace(/\D/g, "").slice(0, 6))}
        onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
        placeholder="••••"
        className="w-full bg-zinc-800 border border-zinc-700 rounded-md px-3 py-2 text-white placeholder-zinc-600 focus:outline-none focus:border-cyan-400 text-sm tracking-widest" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0A0A0A] grain text-white flex items-center justify-center px-6">
      <div className="w-full max-w-sm space-y-6">
        <div className="flex items-center gap-2">
          <KeyRound className="h-5 w-5 text-cyan-400" />
          <h1 className="font-heading font-black text-3xl uppercase tracking-tight">{t("reset.new_title")}</h1>
        </div>

        {!token && <div className="px-4 py-3 bg-red-400/10 border border-red-400/20 rounded-md text-sm text-red-400">{t("reset.bad_link")}</div>}

        {done ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2 px-4 py-3 bg-cyan-400/5 border border-cyan-400/20 rounded-md text-sm text-cyan-300">
              <Check className="h-4 w-4" /> {t("reset.done")}
            </div>
            <button onClick={() => navigate("/")} className="w-full bg-cyan-400 hover:bg-cyan-300 text-black font-bold uppercase tracking-wide py-2.5 rounded-md text-sm">
              {t("reset.back_login")}
            </button>
          </div>
        ) : token ? (
          <div className="space-y-4">
            {field(t("reset.new_pin"), pin, setPin)}
            {field(t("reset.confirm_pin"), confirm, setConfirm)}
            {error && <p className="text-red-400 text-xs bg-red-400/10 border border-red-400/20 rounded-md px-3 py-2">{error}</p>}
            <button onClick={submit} disabled={loading}
              className="w-full bg-cyan-400 hover:bg-cyan-300 disabled:opacity-50 text-black font-bold uppercase tracking-wide py-2.5 rounded-md text-sm">
              {loading ? "..." : t("reset.save")}
            </button>
          </div>
        ) : (
          <button onClick={() => navigate("/")} className="text-sm text-zinc-500 hover:text-white">{t("reset.back_login")}</button>
        )}
      </div>
    </div>
  );
}
